import { Variants, TargetAndTransition } from 'framer-motion';

// Animation Variants
export const fadeInUp: Variants = {
  hidden: {
    opacity: 0,
    y: 20,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: 'easeOut',
    },
  },
};

export const fadeInScale: Variants = {
  hidden: {
    opacity: 0,
    scale: 0.95,
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.4,
      ease: 'easeOut',
    },
  },
};

export const slideInLeft: Variants = {
  hidden: {
    opacity: 0,
    x: -50,
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.5,
      ease: 'easeOut',
    },
  },
};

export const slideInRight: Variants = {
  hidden: {
    opacity: 0,
    x: 50,
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.5,
      ease: 'easeOut',
    },
  },
};

export const staggerContainer: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

export const scaleOnHover: Variants = {
  initial: { scale: 1 },
  hover: {
    scale: 1.05,
    transition: {
      duration: 0.3,
      ease: 'easeInOut',
    },
  },
  tap: {
    scale: 0.98,
  },
};

export const parallaxY: Variants = {
  initial: { y: 0 },
  animate: (offset: number) => ({
    y: offset,
    transition: {
      duration: 0,
    },
  }),
};

// Netflix-style hover animation
export const netflixCardHover: Variants = {
  initial: {
    scale: 1,
    zIndex: 0,
  },
  hover: {
    scale: 1.3,
    zIndex: 10,
    transition: {
      duration: 0.3,
      ease: 'easeInOut',
    },
  },
};

// App Store card animation
export const appStoreCardAnimation: Variants = {
  offscreen: {
    y: 50,
    opacity: 0,
    scale: 0.95,
  },
  onscreen: {
    y: 0,
    opacity: 1,
    scale: 1,
    transition: {
      type: 'spring',
      bounce: 0.4,
      duration: 0.8,
    },
  },
};

// Premium button animation
export const buttonAnimation: Variants = {
  initial: { scale: 1 },
  hover: {
    scale: 1.05,
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
  },
  tap: {
    scale: 0.95,
  },
};

// Gradient animation for backgrounds
export const gradientAnimation: TargetAndTransition = {
  background: [
    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  ],
  transition: {
    duration: 10,
    ease: 'easeInOut',
    repeat: Infinity,
    repeatType: 'reverse',
  },
};

// Shimmer effect for loading states
export const shimmerAnimation: Variants = {
  initial: {
    backgroundPosition: '-200% 0',
  },
  animate: {
    backgroundPosition: '200% 0',
    transition: {
      duration: 2,
      ease: 'linear',
      repeat: Infinity,
    },
  },
};

// Text reveal animation
export const textReveal: Variants = {
  hidden: {
    opacity: 0,
    y: 20,
  },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.05,
      duration: 0.5,
      ease: 'easeOut',
    },
  }),
};

// Floating animation
export const floatingAnimation: TargetAndTransition = {
  y: [0, -10, 0],
  transition: {
    duration: 3,
    ease: 'easeInOut',
    repeat: Infinity,
  },
};

// Pulse animation
export const pulseAnimation: TargetAndTransition = {
  scale: [1, 1.05, 1],
  opacity: [1, 0.8, 1],
  transition: {
    duration: 2,
    ease: 'easeInOut',
    repeat: Infinity,
  },
};

// Spring configs
export const springConfig = {
  default: { type: 'spring', stiffness: 300, damping: 30 },
  gentle: { type: 'spring', stiffness: 100, damping: 20 },
  wobbly: { type: 'spring', stiffness: 200, damping: 10 },
  stiff: { type: 'spring', stiffness: 400, damping: 40 },
};

// Easing functions
export const customEasing = {
  easeInOutQuart: [0.76, 0, 0.24, 1],
  easeOutExpo: [0.19, 1, 0.22, 1],
  easeInOutExpo: [0.87, 0, 0.13, 1],
};

// Animation presets by category
export const animationPresets = {
  hero: {
    fadeInUp,
    fadeInScale,
    parallaxY,
  },
  cards: {
    scaleOnHover,
    netflixCardHover,
    appStoreCardAnimation,
  },
  buttons: {
    buttonAnimation,
    pulseAnimation,
  },
  text: {
    textReveal,
    slideInLeft,
    slideInRight,
  },
  loading: {
    shimmerAnimation,
    floatingAnimation,
  },
  containers: {
    staggerContainer,
  },
};